import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { TicketIcon, CheckCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useTranslations } from '../../hooks/useTranslations';
import toast from 'react-hot-toast';

function PromoCode() {
  const [code, setCode] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { getAuthHeader } = useAdminAuth();
  const { t } = useTranslations();

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('users.copied') || 'Скопировано!');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error(t('users.copyError') || 'Ошибка копирования');
    }
  };

  useEffect(() => {
    fetchCode();
  }, []);

  const fetchCode = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetch('https://api.maxprofitacademy.com/admin/code', {
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch code');
      }

      const result = await response.json();
      
      if (result.status === 200 && result.data) {
        const savedCode = result.data.code || '';
        setCurrentCode(savedCode);
        setCode(savedCode);
      }
    } catch (err) {
      console.error('Error fetching code:', err);
      toast.error(t('promoCode.errorLoadingCode') || 'Ошибка при загрузке кода');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCode = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      toast.error(t('promoCode.emptyCodeError') || 'Введите промо-код');
      return;
    }

    try {
      setIsSaving(true);
      
      const response = await fetch('https://api.maxprofitacademy.com/admin/code', {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: code.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save code');
      }

      const result = await response.json();
      
      if (result.status === 200) {
        setCurrentCode(code.trim());
        toast.success(t('promoCode.successSaved') || 'Промо-код успешно сохранен');
      }
    } catch (err) {
      console.error('Error saving code:', err);
      toast.error(t('promoCode.errorSavingCode') || 'Ошибка при сохранении кода');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <TicketIcon className="h-7 w-7 mr-2 text-primary" />
          {t('promoCode.title') || 'Управление промо-кодом'}
        </h2>
        <p className="text-gray-600 mt-1">
          {t('promoCode.description') || 'Настройте глобальный промо-код для активации специальных цен'}
        </p>
      </div>

      {/* Информационный блок */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">
          {t('promoCode.howItWorks') || 'Как это работает:'}
        </h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>{t('promoCode.info1') || 'По умолчанию цены: 100 / 250 / 500'}</li>
          <li>{t('promoCode.info2') || 'Когда пользователь активирует код: 100 / 250 / 500'}</li>
          <li>{t('promoCode.info3') || 'Только активный код может быть применен пользователями'}</li>
          <li>{t('promoCode.info4') || 'После активации код сохраняется в профиле пользователя'}</li>
        </ul>
      </div>

      {/* Форма управления кодом */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <form onSubmit={handleSaveCode}>
          <div className="space-y-6">
            {/* Поле промо-кода */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                {t('promoCode.codeLabel') || 'Промо-код'}
              </label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t('promoCode.codePlaceholder') || 'Например: SPRING50'}
                className="w-full px-4 py-3 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary uppercase"
                required
              />
            </div>

            {/* Кнопка сохранения */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2 bg-primary text-black font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-black mr-2"></div>
                    {t('promoCode.saving') || 'Сохранение...'}
                  </>
                ) : (
                  t('promoCode.save') || 'Сохранить'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Текущий промо-код */}
      {currentCode && (
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('promoCode.currentStatus') || 'Текущий статус'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">{t('promoCode.activeCode') || 'Активный код'}</p>
              <div className="flex items-center group">
                <p className="text-lg font-bold text-gray-900">{currentCode}</p>
                <button
                  onClick={() => copyToClipboard(currentCode)}
                  className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary"
                  title={t('users.copy') || 'Копировать'}
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">{t('promoCode.status') || 'Статус'}</p>
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-lg font-bold text-green-600">{t('promoCode.active') || 'Активен'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromoCode;
